import { useQuery, useQueryClient } from "@tanstack/react-query";
import { analyticsWebsites } from "../../api/analyticsWebsites";
import { analyticsKey, type AnalyticsScope } from "./queryKeys";
import type { ContextResponse, DateRange } from "./types";
export function useAnalyticsContext(
  scope: AnalyticsScope,
  id: string,
  range: DateRange,
  enabled = true,
) {
  return useQuery({
    queryKey: analyticsKey(scope, "context", id, range),
    queryFn: ({ signal }) => analyticsWebsites.context(id, range, signal),
    enabled: enabled && !!id,
    staleTime: 30000,
  });
}
export function useWebsiteReport<T>(
  scope: AnalyticsScope,
  id: string,
  range: DateRange,
  context: ContextResponse | undefined,
  report: string,
  filters: Record<string, string | number | undefined> = {},
  enabled = true,
) {
  const client = useQueryClient();
  return useQuery({
    queryKey: analyticsKey(
      scope,
      "report",
      id,
      context?.context.mapping_version,
      context?.context.dataset_version,
      range,
      report,
      filters,
    ),
    queryFn: async ({ signal }) => {
      const response = await analyticsWebsites.report<T>(
        id,
        report,
        range,
        filters,
        signal,
      );
      if (
        context &&
        (response.context.mapping_version !== context.context.mapping_version ||
          response.context.dataset_version !== context.context.dataset_version)
      ) {
        void client.invalidateQueries({
          queryKey: analyticsKey(scope, "context", id),
        });
        throw new Error(
          "The report changed while loading. Its latest period and connections are being checked.",
        );
      }
      return response;
    },
    enabled: enabled && !!context,
    staleTime: 30000,
  });
}
